import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { VscLoading } from 'react-icons/vsc';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../../../../../../_helpers';
import ContractorPaymentModal from '../PaymentForm/ContractorPaymentModal';
import ContractorPaymentRow from './ContractorPaymentRow';

function ContractorPaymentsList({
    payments = [],
    setPayments,
    isBusy,
    teamId,
    companyID,
    title = 'Contractor Payments:',
    emptyText = 'No Payments Found',
}) {
    const [showContractorPayment, setShowContractorPayment] = useState({});

    async function handleDeletePayment(paymentData) {
        if (isBusy) {
            return;
        }
        try {
            const resolvedCompanyID = paymentData?.companyID || companyID;
            if (!resolvedCompanyID) {
                throw new Error('Company ID is missing');
            }
            if (!paymentData?.contractorPaymentID) {
                throw new Error('Employee(Contractor) Contractor Payment ID is missing');
            }
            const response = await Swal.fire({
                title: `You are deleting following payment:
                 <br/> <p style="font-size: 14px;">${paymentData.first_name} ${paymentData.last_name} ${paymentData.contractorPaymentID}</p>
                 `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm Delete',
            });
            if (!response.isConfirmed) {
                return;
            }
            await fetchWrapper.delete(
                `/evv/payroll/zeal/contractor/${resolvedCompanyID}/${paymentData.contractorPaymentID}/payments${
                    teamId ? `?team=${teamId}` : ''
                }`
            );
            toast.success(`Payment deleted successfully!`);
            if (setPayments) {
                setPayments((prev) =>
                    (prev || []).filter(
                        ({ contractorPaymentID }) => contractorPaymentID !== paymentData?.contractorPaymentID
                    )
                );
            }
        } catch (error) {
            toast.error(error.message || `Couldn't delete payment!`);
        }
    }

    async function handleApprovePayment(paymentData) {
        if (isBusy) {
            return;
        }
        try {
            const resolvedCompanyID = paymentData?.companyID || companyID;
            if (!resolvedCompanyID) {
                throw new Error('Company ID is missing');
            }
            if (!paymentData?.contractorPaymentID) {
                throw new Error('Employee(Contractor) Contractor Payment ID is missing');
            }
            const response = await Swal.fire({
                title: `You are approving following payment:
                 <br/> <p style="font-size: 14px;">${paymentData.first_name} ${paymentData.last_name} ${paymentData.contractorPaymentID}</p>
                 `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm Approve',
            });
            if (!response.isConfirmed) {
                return;
            }

            const res = await fetchWrapper.patch(
                `/evv/payroll/zeal/contractor/${resolvedCompanyID}/${paymentData.contractorPaymentID}/payments${
                    teamId ? `?team=${teamId}` : ''
                }`
            );
            toast.success(`Payment approved successfully!`);
            if (setPayments) {
                setPayments((prev) =>
                    (prev || []).map((payment) =>
                        payment?.contractorPaymentID === res?.contractorPaymentID ? { ...res } : payment
                    )
                );
            }
        } catch (error) {
            toast.error(error.message || `Couldn't delete payment!`);
        }
    }

    useEffect(() => {
        if (showContractorPayment?.mode === 'delete' && showContractorPayment?.data?.contractorPaymentID) {
            handleDeletePayment(showContractorPayment.data);
        }
        if (showContractorPayment?.mode === 'approve' && showContractorPayment?.data?.contractorPaymentID) {
            handleApprovePayment(showContractorPayment.data);
        }
    }, [showContractorPayment]);

    const sortedPayments = useMemo(() => {
        return [...(payments || [])].sort((c1, c2) => new Date(c2?.pay_date || 0) - new Date(c1?.pay_date || 0));
    }, [payments]);

    return (
        <div
            className="w-100 d-flex flex-column align-items-center"
            css={css`
                overflow-x: hidden;
                gap: 12px;
                padding: 12px;
            `}
        >
            <div className="d-flex flex-row w-100 align-items-end justify-content-between">
                <div>
                    <div>
                        <strong>{title}</strong>
                    </div>
                </div>
            </div>
            <Fragment>
                {isBusy ? (
                    <VscLoading
                        className="spin"
                        size="24px"
                        css={css`
                            overflow: hidden;
                        `}
                    />
                ) : (
                    <Fragment>
                        {sortedPayments?.length ? (
                            <div
                                css={css`
                                    width: 100%;
                                    display: flex;
                                    flex-direction: column;
                                    gap: 12px;
                                `}
                            >
                                {sortedPayments.map((contractorPayment, index) => (
                                    <ContractorPaymentRow
                                        key={index}
                                        contractorPayment={contractorPayment}
                                        setShowContractorPayment={(data, mode = 'view') => {
                                            setShowContractorPayment({ data, mode });
                                        }}
                                        companyID={companyID}
                                        teamId={teamId}
                                        fromPayroll={true}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div>{emptyText}</div>
                        )}
                    </Fragment>
                )}
            </Fragment>
            <ContractorPaymentModal
                show={
                    !!showContractorPayment?.data?.contractorID &&
                    ['view', 'edit'].includes(showContractorPayment?.mode)
                }
                handleClose={() => {
                    setShowContractorPayment({});
                }}
                companyID={companyID}
                contractorPayment={showContractorPayment?.data || {}}
                mode={showContractorPayment?.mode || 'view'}
                teamId={teamId}
                handleUpdatePayments={(updatedPaymentData) => {
                    if (!setPayments) return;
                    setPayments((prev) =>
                        (prev || []).map((payment) =>
                            payment?.contractorPaymentID === updatedPaymentData?.contractorPaymentID
                                ? { ...updatedPaymentData }
                                : payment
                        )
                    );
                }}
            />
        </div>
    );
}

export default ContractorPaymentsList;
